import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QualificationItem from './QualificationItem';

export default function QualificationForm({ formData, onChange }) {
  const [qualifications, setQualifications] = useState(
    formData.skill_sheet_qualification_infos_attributes || formData.skill_sheet_qualification_infos || []
  );

  // 表示されている資格のみをフィルタリング
  const visibleQualifications = useMemo(() => {
    return qualifications.filter(q => !q._destroy);
  }, [qualifications]);

  // 保存ボタンが押されたかどうかの状態
  const [showValidation, setShowValidation] = useState(false);
  // 最後の更新アクションを追跡するためのフラグ
  const [lastAction, setLastAction] = useState(null);

  // 親コンポーネントのvalidationError変更を検知
  useEffect(() => {
    // 親でバリデーションエラーがセットされたら、自分のshowValidationをtrueにする
    if (formData.validationTriggered) {
      setShowValidation(true);
    }
  }, [formData.validationTriggered]);

  // 表示されている資格の数をチェック
  useEffect(() => {
    // すべての資格に削除フラグがついている場合は、親コンポーネントに資格情報を渡す
    if (qualifications.length > 0 && visibleQualifications.length === 0) {
      onChange({
        skill_sheet_qualification_infos_attributes: qualifications,
        validationTriggered: false
      });
    }
  }, [qualifications, visibleQualifications.length]);

  const handleAddQualification = () => {
    // すべての資格が削除済み（_destroy=true）か判定する
    const allDestroyed = qualifications.length > 0 && visibleQualifications.length === 0;

    // すべての資格が削除済みの場合は、既に登録済みだが削除した資格を取得
    // すべての資格が削除済みではない場合は、すべての資格を取得
    const filteredQualifications = allDestroyed ? qualifications.filter(q => q._destroy && q.id) : qualifications;

    // 新しい資格を追加
    const newQualification = { name: '', acquisition_date: '', _destroy: false };

    const newQualifications = [...filteredQualifications, newQualification];

    setQualifications(newQualifications);

    onChange({
      skill_sheet_qualification_infos_attributes: newQualifications,
      validationTriggered: false
    });
  };

  // 表示されている資格の実際のインデックスを見つける関数
  const findActualIndex = (visibleIndex) => {
    const realQualification = visibleQualifications[visibleIndex];
    return qualifications.findIndex(q => q === realQualification);
  };

  const handleQualificationChange = (index, updatedQualification, options = {}) => {
    // 表示されている資格の実際のインデックスを取得
    const actualIndex = findActualIndex(index);
    if (actualIndex === -1) return;

    const newQualifications = [...qualifications];
    newQualifications[actualIndex] = updatedQualification;
    setQualifications(newQualifications);

    // 入力変更時にバリデーションをリセットする
    if (options && options.resetValidation) {
      setShowValidation(false);
      // 変更があった場合は直接onChangeを呼び出して即時反映させる
      onChange({
        skill_sheet_qualification_infos_attributes: newQualifications,
        validationTriggered: false
      });
    } else {
      // 通常の更新（バリデーション状態を維持）
      onChange({
        skill_sheet_qualification_infos_attributes: newQualifications,
        validationTriggered: formData.validationTriggered
      });
    }
  };

  const handleRemoveQualification = (index) => {
    // 表示されている資格の実際のインデックスを取得
    const actualIndex = findActualIndex(index);
    if (actualIndex === -1) return;

    const newQualifications = [...qualifications];
    if (newQualifications[actualIndex].id) {
      // 既存のレコードの場合は_destroyフラグを立てる
      newQualifications[actualIndex] = {
        ...newQualifications[actualIndex],
        _destroy: true
      };
    } else {
      // 新しいレコードの場合は配列から削除
      newQualifications.splice(actualIndex, 1);
    }
    setQualifications(newQualifications);

    onChange({
      skill_sheet_qualification_infos_attributes: newQualifications,
      validationTriggered: false
    });

    setShowValidation(false);
  };

  // 親コンポーネントからのバリデーションエラーを確認
  const hasValidationErrors = qualifications.some(q =>
    !q._destroy && (
      // 資格名が空
      (!q.name || q.name.trim() === '') ||
      // 資格名があるのに取得日が空
      (q.name && q.name.trim() !== '' && (!q.acquisition_date || q.acquisition_date.trim() === ''))
    )
  );

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation) {
    const hasNameErrors = qualifications.some(q => !q._destroy && (!q.name || q.name.trim() === ''));
    const hasDateErrors = qualifications.some(q => !q._destroy && (!q.acquisition_date || q.acquisition_date.trim() === ''));

    if (hasNameErrors) validationErrors.push('資格名は入力必須です。');
    if (hasDateErrors) validationErrors.push('取得日は入力必須です。');
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        資格
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {visibleQualifications.map((qualification, index) => (
          <QualificationItem
            key={qualification.id || `new-${index}`}
            qualification={qualification}
            index={index}
            onChange={handleQualificationChange}
            onRemove={handleRemoveQualification}
            showValidation={showValidation}
          />
        ))}
      </Box>

      <Button
        variant="contained"
        sx={{
          mb: 2,
          backgroundColor: '#818cf8',
          '&:hover': {
            backgroundColor: '#6366f1'
          }
        }}
        startIcon={<AddIcon />}
        onClick={handleAddQualification}
      >
        資格を追加
      </Button>
    </Box>
  );
}
